import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import HeroSection from "../components/HeroSection";
import ContentCards from "../components/ContentCard";
import VideoGrid from "../components/VideoGrid";
import VideoModal from "../components/VideoModal";
import PopupModal from "../components/PopupModal";
import { characterImages } from "../data/characters";
import CharacterCard from "../components/CharacterCard";
import ScrollToTop from "../components/ScrollToTop";
import boil from "../assets/images/boil.png";
import star from "../assets/images/star.png";
import glue from "../assets/images/glue.png";
import pear from "../assets/images/pear.png";

const CHANNEL_ID = "UCXFOpp-skK2-psYqVwYOj1g";
const MAX_RESULTS = 4;

const PAGE_META = {
    EN: {
        title: "Hanapin | Free Japanese Lessons for Kids & Beginners – Hiragana, Katakana, Games & Worksheets",
        desc:  "Free Japanese lessons for kids and beginners! Practice hiragana and katakana with interactive games, stroke-order writing, vocabulary flashcards, and free printable worksheets.",
    },
    JP: {
        title: "はなぴん | 無料の日本語レッスン – ひらがな・カタカナ・ゲーム・プリント",
        desc:  "はなぴんと一緒に楽しく日本語を学ぼう！ひらがな・カタカナの読み書き練習、ゲーム、単語カード、無料プリントが全部無料で使えます。",
    },
    ZH: {
        title: "樱花老师 | 免费日语课 – 平假名・片假名・游戏・练习字帖",
        desc:  "免费学日语！练习平假名和片假名、互动游戏、单词卡、可打印练习字帖，适合儿童和初学者。",
    },
};

export default function Home({ language, texts }) {
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [selectedNews, setSelectedNews] = useState(null);
    const [videos, setVideos] = useState([]);

    const s = texts?.sections || {};

    // ── Per-page SEO ──────────────────────────────────────────────────────────
    useEffect(() => {
        const meta = PAGE_META[language] || PAGE_META.EN;
        document.title = meta.title;
        const el = document.querySelector('meta[name="description"]');
        if (el) el.setAttribute("content", meta.desc);
    }, [language]);

    useEffect(() => {
        const fetchVideos = async () => {
            try {
                const rssUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${CHANNEL_ID}`;
                const res = await fetch(
                    `https://api.allorigins.win/get?url=${encodeURIComponent(rssUrl)}`
                );
                const json = await res.json();
                const xml = new DOMParser().parseFromString(json.contents, "text/xml");
                const entries = Array.from(xml.querySelectorAll("entry")).slice(0, MAX_RESULTS);
                const formatted = entries.map((entry) => {
                    const link = entry.querySelector("link")?.getAttribute("href") || "";
                    const videoId = link.split("v=")[1] ||
                        entry.querySelector("id")?.textContent?.split(":").pop();
                    const title = entry.querySelector("title")?.textContent || "";
                    return {
                        videoId,
                        title,
                        thumb: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
                    };
                }).filter((v) => v.videoId);
                if (formatted.length > 0) setVideos(formatted);
            } catch (error) {
                console.error("Error fetching YouTube videos:", error);
            }
        };

        fetchVideos();
    }, []);

    return (
        <div className="relative">
            {/* メイン */}
            <HeroSection language={language} texts={texts} />

            {/* 他の動画 */}
            <section className="relative bg-[#fdcb6e] mt-0 px-6 py-12 md:px-12 overflow-hidden">
                <h2 className="text-3xl font-bold mb-6 text-center">
                    {s.otherLessons}
                </h2>

                <motion.img
                    src={boil}
                    alt=""
                    aria-hidden="true"
                    className="absolute w-32 top-10 left-10"
                    animate={{ y: [50, 10, 0], rotate: [10, 30, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.img
                    src={pear}
                    alt=""
                    aria-hidden="true"
                    className="absolute w-48 bottom-0 right-8"
                    animate={{ y: [0, -10, 0, 10, 0], rotate: [0, 5, 0, -5, 0] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />

                <VideoGrid
                    videos={videos}
                    onSelect={(videoId) => setSelectedVideo({ videoId })}
                />

                <div className="flex justify-center mt-8">
                    <a
                        href="https://www.youtube.com/channel/UCXFOpp-skK2-psYqVwYOj1g"
                        className="px-6 py-2 border-2 border-black bg-[#b5435f] text-black rounded-full font-semibold hover:bg-[#a2364f] transition"
                    >
                        {s.watchYouTube}
                    </a>
                </div>
            </section>

            {/* コンテンツカード */}
            <section id="contents" className="relative px-6 py-12 md:px-12">
                <motion.img
                    src={star}
                    alt=""
                    aria-hidden="true"
                    className="absolute w-32 top-10 left-10"
                    animate={{ y: [50, 10, 0], rotate: [10, 30, 0] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.img
                    src={glue}
                    alt=""
                    aria-hidden="true"
                    className="absolute w-48 bottom-0 right-8"
                    animate={{ y: [0, -10, 0, 10, 0], rotate: [0, 5, 0, -5, 0] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />

                <h2 className="text-3xl font-bold mb-6 text-center">
                    {s.studyMaterials}
                </h2>

                <div className="relative flex justify-center mt-10">
                    <ContentCards cards={texts?.cards || []} />
                </div>
            </section>

            {/* お知らせ */}
            <section className="relative bg-[#ee9cee] px-6 py-12 md:px-12">
                <h2 className="text-3xl font-bold mb-6 text-center">
                    {s.news}
                </h2>

                <div className="flex flex-col divide-y divide-w-300 max-w-3xl mx-auto">
                    {texts?.news?.map((item, i) => (
                        <button
                            key={i}
                            onClick={() => setSelectedNews(item)}
                            className="flex items-center justify-between py-4 px-4 w-full text-left transition"
                        >
                            <span className="text-black-700 w-24">{item.date}</span>
                            <span className="flex-1 text-black font-medium">{item.title}</span>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-6 h-6 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    ))}
                </div>

                <PopupModal item={selectedNews} onClose={() => setSelectedNews(null)} />
            </section>

            {/* キャラクター紹介 */}
            <section id="character" className="relative bg-[#94edfe] px-6 py-12 md:px-12">
                <h2 className="text-3xl font-bold mb-6 text-center">
                    {s.characters}
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                    {Object.entries(texts.characters).map(([key, c], i) => (
                        <CharacterCard
                            key={key}
                            character={{
                                ...c,
                                image: characterImages[key]?.image,
                                ...(key === 'sakura' ? {
                                    bilibili: "https://space.bilibili.com/3706935259957265",
                                    xiaohongshu: "https://xhslink.com/m/4gsmgkmH0GL",
                                } : {}),
                            }}
                            delay={i * 0.2}
                        />
                    ))}
                </div>
            </section>
            <VideoModal
                videoId={selectedVideo?.videoId}
                onClose={() => setSelectedVideo(null)}
            />
            <ScrollToTop />
        </div>
    );
}
